/**
 * TagsEditor Component
 *
 * Multi-select editor for task tags with search and create functionality.
 */

import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Tag } from 'lucide-react';
import clsx from 'clsx';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';

export interface TagsEditorProps {
  tags: string[];
  availableTags?: string[];
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
  /** Called when a brand-new tag is created (not selected from availableTags) */
  onCreateNew?: (tagName: string) => void;
  size?: 'sm' | 'md';
  maxDisplay?: number;
  disabled?: boolean;
}

export function TagsEditor({
  tags,
  availableTags = [],
  onAdd,
  onRemove,
  onCreateNew,
  size = 'md',
  maxDisplay = 3,
  disabled = false,
}: TagsEditorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 8,
    minWidth: 200,
    matchTriggerWidth: false,
  });

  useClickOutside(
    [dropdownRef, triggerRef],
    () => {
      setIsOpen(false);
      setSearchTerm('');
    },
    isOpen
  );

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const filteredTags = availableTags.filter(
    (t) => !tags.includes(t) && t.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedTagsFiltered = tags.filter((t) =>
    t.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddNew = () => {
    const trimmed = searchTerm.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onAdd(trimmed);
      onCreateNew?.(trimmed);
      setSearchTerm('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (filteredTags.length > 0) {
        onAdd(filteredTags[0]);
        setSearchTerm('');
      } else {
        handleAddNew();
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const tagSize = size === 'sm' ? 'px-1.5 text-[9px]' : 'px-2 text-[10px]';
  const containerHeight = size === 'sm' ? 'h-6' : 'h-7';

  return (
    <div
      ref={triggerRef}
      className={clsx(
        'relative inline-flex items-center gap-1 rounded-md bg-surface-tertiary overflow-hidden',
        containerHeight,
        'px-1'
      )}
    >
      {/* Tags Container - with overflow */}
      <div className="flex items-center gap-1 min-w-0 overflow-hidden">
        {tags.length === 0 ? (
          <span
            className={clsx(
              'text-text-quaternary flex items-center gap-1 shrink-0',
              size === 'sm' ? 'text-[10px]' : 'text-[11px]'
            )}
          >
            <Tag className={clsx(size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5')} />
            {disabled ? '—' : ''}
          </span>
        ) : (
          <>
            {tags.slice(0, maxDisplay).map((tag) => (
              <span
                key={tag}
                className={clsx(
                  'inline-flex items-center gap-1 rounded bg-accent-violet/10 text-accent-violet font-medium group shrink-0 h-4 min-w-0 max-w-[80px]',
                  tagSize,
                  !disabled && 'cursor-pointer hover:bg-accent-violet/20'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) onRemove(tag);
                }}
                title={disabled ? tag : `Rimuovi "${tag}"`}
              >
                <span className="truncate min-w-0 flex-1">{tag}</span>
                {!disabled && (
                  <X className="w-2.5 h-2.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </span>
            ))}
            {tags.length > maxDisplay && (
              <span className={clsx('text-text-quaternary font-medium shrink-0', tagSize)}>
                +{tags.length - maxDisplay}
              </span>
            )}
          </>
        )}
      </div>

      {/* Add Button - always visible */}
      {!disabled && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          className={clsx(
            'inline-flex items-center justify-center rounded hover:bg-surface-hover text-text-quaternary hover:text-accent-violet transition-all shrink-0',
            size === 'sm' ? 'w-4 h-4' : 'w-5 h-5',
            tags.length === 0 && 'ml-0.5'
          )}
          title="Aggiungi tag"
        >
          <Plus className="w-3 h-3" />
        </button>
      )}

      {/* Dropdown */}
      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-lg shadow-2xl min-w-[220px] max-w-[280px] animate-fade-in border border-border-primary/70 bg-surface-primary overflow-hidden"
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-border-primary/50">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Cerca o crea tag..."
                className="w-full px-2.5 py-1.5 text-[11px] bg-surface-tertiary border border-border-primary/50 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet focus:border-accent-violet text-text-primary placeholder-text-quaternary transition-all"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Content */}
            <div className="max-h-[240px] overflow-y-auto">
              {/* Selected Tags Section */}
              {selectedTagsFiltered.length > 0 && (
                <div className="px-2 pt-2 pb-1">
                  <div className="px-2 py-1 text-[10px] font-semibold text-text-quaternary uppercase tracking-wider">
                    Selezionati ({selectedTagsFiltered.length})
                  </div>
                  <div className="space-y-0.5">
                    {selectedTagsFiltered.map((tag) => (
                      <button
                        key={tag}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onRemove(tag);
                        }}
                        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-accent-violet/10 transition-colors group"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent-violet shrink-0" />
                          <span className="text-[11px] text-text-primary truncate">{tag}</span>
                        </div>
                        <X className="w-3 h-3 text-text-quaternary group-hover:text-accent-rose shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Tags Section */}
              {(filteredTags.length > 0 ||
                (searchTerm &&
                  !filteredTags.some((t) => t.toLowerCase() === searchTerm.toLowerCase()) &&
                  !tags.includes(searchTerm.trim()))) && (
                <div
                  className={clsx(
                    'px-2',
                    selectedTagsFiltered.length > 0 && 'pt-2 pb-2 border-t border-border-primary/30'
                  )}
                >
                  {selectedTagsFiltered.length > 0 && (
                    <div className="px-2 py-1 text-[10px] font-semibold text-text-quaternary uppercase tracking-wider">
                      Disponibili
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {filteredTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAdd(tag);
                          setSearchTerm('');
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                      >
                        <Tag className="w-3 h-3 text-accent-violet shrink-0" />
                        <span className="text-[11px] text-text-primary truncate">{tag}</span>
                      </button>
                    ))}
                    {searchTerm &&
                      !filteredTags.some((t) => t.toLowerCase() === searchTerm.toLowerCase()) &&
                      !tags.includes(searchTerm.trim()) && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddNew();
                          }}
                          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-accent-violet/10 transition-colors text-accent-violet"
                        >
                          <Plus className="w-3 h-3 shrink-0" />
                          <span className="text-[11px] font-medium">Crea "{searchTerm}"</span>
                        </button>
                      )}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {filteredTags.length === 0 && selectedTagsFiltered.length === 0 && !searchTerm && (
                <div className="px-3 py-4 text-center">
                  <Tag className="w-4 h-4 text-text-quaternary mx-auto mb-1.5 opacity-50" />
                  <div className="text-[11px] text-text-quaternary">Nessun tag disponibile</div>
                </div>
              )}
              {filteredTags.length === 0 &&
                selectedTagsFiltered.length === 0 &&
                searchTerm &&
                tags.includes(searchTerm.trim()) && (
                  <div className="px-3 py-4 text-center">
                    <div className="text-[11px] text-text-quaternary">Tag già selezionato</div>
                  </div>
                )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
